import { useQuery } from '@powersync/react';
import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { Divider } from '@/components/ui/Divider';
import { Text } from '@/components/ui/Text';
import { confirmDestructive } from '@/lib/confirm';
import { type RosterMember } from '@/lib/data';
import { type TemplateRole } from '@rbf/shared';

import { RolePicker } from './RolePicker';

export interface AccessRosterProps {
  /** Section label, e.g. "People with access". */
  title: string;
  /** Template whose live membership drives the roster refresh. */
  templateId: string;
  /** Admin-only roster fetch (a SECURITY DEFINER RPC), including the owner. */
  load: () => Promise<RosterMember[]>;
  /** Remove one collaborator. */
  onRemove: (member: RosterMember) => Promise<void>;
  /** Change one collaborator's role. */
  onChangeRole: (member: RosterMember, role: TemplateRole) => Promise<void>;
  /** Change to force a reload (e.g. when the sheet opens or a link is minted). */
  reloadKey?: unknown;
  emptyLabel?: string;
}

/**
 * Admin-facing access manager: lists everyone with access (with names, via the
 * roster RPC — the admin doesn't sync others' profiles) and their role. The
 * owner is shown but not editable; every other member can be re-roled or
 * removed. Revoking the invite links themselves lives in the ShareSheet.
 */
export function AccessRoster({
  title,
  templateId,
  load,
  onRemove,
  onChangeRole,
  reloadKey,
  emptyLabel = 'No one else has access yet.',
}: AccessRosterProps) {
  const [members, setMembers] = useState<RosterMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Membership syncs live to admins; watch it so the roster refetches (for the
  // names, which don't sync) as soon as someone joins, leaves, or is re-roled. A
  // primitive signature — not the array identity — keeps the effect from refiring
  // on every render.
  const { data: collaborators } = useQuery<{ user_id: string; role: string }>(
    'SELECT user_id, role FROM template_collaborators WHERE template_id = ? ORDER BY user_id',
    [templateId],
  );
  const membershipSig = collaborators.map((c) => `${c.user_id}:${c.role}`).join(',');

  // `load` is an inline arrow from the parent (new identity each render), so read
  // it through a ref and key the fetch off `reloadKey` + the live membership —
  // otherwise the effect would refetch on every render.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  async function refresh() {
    try {
      setMembers(await loadRef.current());
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const rows = await loadRef.current();
        if (active) setMembers(rows);
      } catch (e) {
        if (active) setError((e as Error).message);
      }
    })();
    return () => {
      active = false;
    };
  }, [reloadKey, membershipSig]);

  async function remove(member: RosterMember) {
    const name = member.display_name ?? 'this person';
    if (!(await confirmDestructive('Remove access?', `Remove ${name}?`, 'Remove'))) {
      return;
    }
    setBusyId(member.user_id);
    setError(null);
    try {
      await onRemove(member);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function changeRole(member: RosterMember, role: TemplateRole) {
    if (role === member.role) return;
    setBusyId(member.user_id);
    setError(null);
    try {
      await onChangeRole(member, role);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <View className="gap-2">
      <Text variant="label">{title}</Text>

      {members === null ? (
        <Text variant="caption" className="text-faint">
          Loading…
        </Text>
      ) : members.length === 0 ? (
        <Text variant="caption" className="text-faint">
          {emptyLabel}
        </Text>
      ) : (
        <View className="rounded-xl border border-border bg-card">
          {members.map((m, i) => (
            <View key={m.user_id}>
              {i > 0 ? <Divider /> : null}
              <View className="gap-2 px-3 py-3">
                <View className="flex-row items-center justify-between gap-2">
                  <Text variant="bodyMedium" numberOfLines={1} className="flex-1">
                    {m.display_name ?? 'Unknown user'}
                  </Text>
                  {m.role === 'owner' ? (
                    <Text variant="caption" className="text-faint">
                      Owner
                    </Text>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      label="Remove"
                      busy={busyId === m.user_id}
                      onPress={() => remove(m)}
                    />
                  )}
                </View>
                {m.role === 'owner' ? null : (
                  <RolePicker
                    compact
                    value={m.role as TemplateRole}
                    disabled={busyId === m.user_id}
                    onChange={(role) => changeRole(m, role)}
                  />
                )}
              </View>
            </View>
          ))}
        </View>
      )}

      {error ? (
        <Text variant="caption" className="text-alert">
          {error}
        </Text>
      ) : null}
    </View>
  );
}
